import React, { useMemo } from 'react';
import {
  buildWeeklyMenu,
  getPlanMenuSlotIds,
  getSlotPricingRow,
} from '../../data/specialtyMealPlans';
import { expandToFullWeek, MEAL_SLOT_TYPES, WEEKDAY_ORDER } from '../../data/mealSlotTypes';
import MealSlotBadge from './MealSlotBadge';

const WEEKDAYS = WEEKDAY_ORDER;

const TABLE_SLOT_LABELS = {
  breakfast: 'Breakfast',
  mid_morning: 'Mid Morning Snack',
  lunch: 'Lunch',
  evening_snack: 'Evening Snack',
  dinner: 'Dinner',
};

function MealCell({ meal }) {
  if (!meal?.item || meal.item === '—') {
    return <span className="sp-weekly-table__empty">—</span>;
  }
  return (
    <div className="sp-weekly-table__meal">
      <strong>{meal.item}</strong>
      {meal.portion ? <span>{meal.portion}</span> : null}
      {meal.calories ? <em>{meal.calories}</em> : null}
    </div>
  );
}

function formatInr(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatInrPerDay(value) {
  const amount = formatInr(value);
  return amount === '—' ? amount : `${amount}/day`;
}

function SlotColumnHeader({ label, price, useOrganic }) {
  return (
    <th scope="col" className={`sp-weekly-table__slot-col${useOrganic ? ' sp-weekly-table__slot-col--organic' : ''}`}>
      <span className="sp-weekly-table__slot-col-label">{label}</span>
      <span className="sp-weekly-table__slot-col-price">{formatInrPerDay(price)}</span>
    </th>
  );
}

export default function WeeklyMenuTable({
  plan,
  singleSlotId = null,
  /** 'bySlot' = slots as rows (legacy); 'byDay' = weekdays as rows, slots as columns */
  orientation = 'bySlot',
  useOrganic = false,
}) {
  const weeklyMenu = useMemo(() => {
    const raw = plan?.weeklyMenu?.length ? plan.weeklyMenu : buildWeeklyMenu(plan);
    return expandToFullWeek(raw);
  }, [plan]);

  const mealByDaySlot = useMemo(() => {
    const map = {};
    for (const { day, meals } of weeklyMenu) {
      map[day] = {};
      for (const meal of meals || []) {
        if (meal.slotId) map[day][meal.slotId] = meal;
      }
    }
    return map;
  }, [weeklyMenu]);

  const menuSlotIds = useMemo(() => {
    if (singleSlotId) return [singleSlotId];
    const fromMenu = getPlanMenuSlotIds(plan);
    if (fromMenu.length) return fromMenu;
    return MEAL_SLOT_TYPES.map((t) => t.id);
  }, [plan, singleSlotId]);

  const slotColumns = useMemo(() => (
    menuSlotIds.map((slotId) => {
      const slotType = MEAL_SLOT_TYPES.find((t) => t.id === slotId);
      const priceRow = getSlotPricingRow(plan, slotId);
      return {
        slotId,
        label: TABLE_SLOT_LABELS[slotId] || slotType?.label || slotId,
        price: useOrganic ? priceRow?.perDayOrganic : priceRow?.perDayNonOrganic,
      };
    })
  ), [menuSlotIds, plan, useOrganic]);

  const tableRows = useMemo(() => {
    const slots = singleSlotId
      ? MEAL_SLOT_TYPES.filter((t) => t.id === singleSlotId)
      : MEAL_SLOT_TYPES;
    return slots.map((slotType) => {
      const priceRow = getSlotPricingRow(plan, slotType.id);
      return {
        slotType,
        label: TABLE_SLOT_LABELS[slotType.id] || slotType.label,
        organic: priceRow?.perDayOrganic,
        nonOrganic: priceRow?.perDayNonOrganic,
        cells: WEEKDAYS.map((day) => mealByDaySlot[day]?.[slotType.id] || null),
      };
    });
  }, [plan, mealByDaySlot, singleSlotId]);

  if (!weeklyMenu.length) {
    return <p className="sp-muted">Weekly menu will be shared when you subscribe.</p>;
  }

  if (orientation === 'byDay') {
    const rowDays = WEEKDAYS;

    return (
      <div className="sp-weekly-table-wrap">
        <div className="sp-weekly-table-scroll sp-weekly-table-scroll--sticky-head">
          <table className={`sp-weekly-table sp-weekly-table--by-day${useOrganic ? ' sp-weekly-table--organic-prices' : ''}`}>
            <thead>
              <tr>
                <th className="sp-weekly-table__corner sp-weekly-table__corner--day" scope="col">
                  Day
                </th>
                {slotColumns.map((col) => (
                  <SlotColumnHeader
                    key={col.slotId}
                    label={col.label}
                    price={col.price}
                    useOrganic={useOrganic}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {rowDays.map((day) => (
                <tr key={day}>
                  <th scope="row" className="sp-weekly-table__day-label">{day}</th>
                  {slotColumns.map((col) => (
                    <td key={`${day}-${col.slotId}`}>
                      <MealCell meal={mealByDaySlot[day]?.[col.slotId] || null} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="sp-delivery-note">
          Prices shown are per delivery day (
          <span className="sp-delivery-note__meal-type">{useOrganic ? 'Organic' : 'Regular'}</span>
          ). * Delivery charges extra
        </p>
      </div>
    );
  }

  return (
    <div className="sp-weekly-table-wrap">
      <div className="sp-weekly-table-scroll">
        <table className="sp-weekly-table">
          <thead>
            <tr>
              <th className="sp-weekly-table__corner" scope="col" />
              {WEEKDAYS.map((day) => (
                <th key={day} scope="col">{day}</th>
              ))}
              <th scope="col" className="sp-weekly-table__col-price sp-weekly-table__col-price--organic">
                Organic Price
              </th>
              <th scope="col" className="sp-weekly-table__col-price">
                Non Organic Price
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row) => (
              <tr key={row.slotType.id}>
                <th scope="row" className="sp-weekly-table__slot">
                  <MealSlotBadge slotId={row.slotType.id} slot={row.label} compact />
                </th>
                {row.cells.map((meal, index) => (
                  <td key={WEEKDAYS[index]}>
                    <MealCell meal={meal} />
                  </td>
                ))}
                <td className="sp-weekly-table__price sp-weekly-table__price--organic">
                  {formatInr(row.organic)}
                  <span className="sp-weekly-table__per-day">/ day</span>
                </td>
                <td className="sp-weekly-table__price">
                  {formatInr(row.nonOrganic)}
                  <span className="sp-weekly-table__per-day">/ day</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="sp-delivery-note">* Delivery charges extra</p>
    </div>
  );
}
